import {
  useGetPrestations,
  useGetPrestationsList,
  useUpdatePrestationPriceMutation,
} from "@ac-expo/api";
import {
  BackofficePrestation,
  GetPrestations,
  isBackofficePrestationType,
  Paginated,
  Searchable,
  Sortable,
} from "@ac-expo/api-types";
import { getExcludedTaxesPrice } from "@ac-expo/utils";
import { BackofficePrestationType } from "@prisma/client";
import { useCallback } from "react";

interface UsePrestationsOptions {
  filters?: Partial<Sortable<Paginated<Searchable>>>;
  includeList?: boolean;
}

interface UsePrestationsReturn {
  prestations: GetPrestations | undefined;
  prestationsList: BackofficePrestation[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;

  totalCount: number;
  pageIndex: number | undefined;
  pageSize: number | undefined;

  getPrestation: (
    type: BackofficePrestationType
  ) => BackofficePrestation | undefined;
  getPrestationBasePrice: (type: BackofficePrestationType) => number;
  getPrestationExcludedTaxesBasePrice: (
    type: BackofficePrestationType
  ) => number;

  updatePrestation: ReturnType<
    typeof useUpdatePrestationPriceMutation
  >["mutate"];
  isUpdating: boolean;

  refetch: () => void;
}

export const usePrestations = (
  options: UsePrestationsOptions = {}
): UsePrestationsReturn => {
  const { filters, includeList: includeListOption } = options;
  const hasListFilters =
    filters?.searchText !== undefined ||
    filters?.pagination !== undefined ||
    filters?.sorting !== undefined;
  const includeList = includeListOption ?? hasListFilters;

  const {
    data: prestations,
    isLoading: isLoadingAll,
    isError: isErrorAll,
    error: errorAll,
    refetch: refetchAll,
  } = useGetPrestations();

  const listFilters = {
    searchText: filters?.searchText,
    pagination: filters?.pagination,
    sorting: filters?.sorting,
  } as Sortable<Paginated<Searchable>>;

  const {
    data: prestationsListData,
    isLoading: isLoadingList,
    isError: isErrorList,
    error: errorList,
    refetch: refetchList,
  } = useGetPrestationsList(listFilters, { enabled: includeList });

  const { mutate: updatePrestation, isPending: isUpdating } =
    useUpdatePrestationPriceMutation();

  const isLoading = isLoadingAll || (includeList && isLoadingList);
  const isError = isErrorAll || (includeList && isErrorList);
  const error = (errorAll || (includeList ? errorList : null)) as Error | null;

  const getPrestation = useCallback(
    (type: BackofficePrestationType) => {
      if (!prestations) return undefined;
      if (!isBackofficePrestationType(type)) {
        throw new Error(`Invalid prestation type: ${type}`);
      }
      if (!prestations[type]) {
        throw new Error(`Prestation not found: ${type}`);
      }
      return prestations[type];
    },
    [prestations]
  );

  const getPrestationBasePrice = useCallback(
    (type: BackofficePrestationType) => {
      const prestation = getPrestation(type);
      if (!prestation?.pricing?.price) return 0;
      return prestation.pricing.price.priceTTC;
    },
    [getPrestation]
  );

  const getPrestationExcludedTaxesBasePrice = useCallback(
    (type: BackofficePrestationType) => {
      const prestation = getPrestation(type);
      if (!prestation?.pricing?.price) return 0;

      const priceTTC = prestation.pricing.price.priceTTC;
      const vatRate = prestation.pricing.price.vatRate;

      return getExcludedTaxesPrice(priceTTC, vatRate);
    },
    [getPrestation]
  );

  const refetch = useCallback(() => {
    refetchAll();
    if (includeList) {
      refetchList();
    }
  }, [includeList, refetchAll, refetchList]);

  return {
    prestations,
    prestationsList: includeList ? prestationsListData?.data : undefined,
    isLoading,
    isError,
    error,

    totalCount: includeList ? prestationsListData?.count || 0 : 0,
    pageIndex: includeList
      ? (prestationsListData?.pageIndex ?? undefined)
      : undefined,
    pageSize: includeList
      ? (prestationsListData?.pageSize ?? undefined)
      : undefined,

    getPrestation,
    getPrestationBasePrice,
    getPrestationExcludedTaxesBasePrice,

    updatePrestation,
    isUpdating,

    refetch,
  };
};
